import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BannerDetail, BannerCreateParams, BannerTarget, BannerType, BannerUpdateParams, ProductDropdownItem } from '@models';
import { ButtonClickEvent, CacBaseDialogComponent, CacDialogLayoutComponent, CacFieldComponent, CacImageUploaderComponent, CacSwitchComponent, CacControlBuilderComponent } from '@controladad/ng-base';
import { BannerApiService, CategoryApiService, FileType, UploadResult, AdminProductsApiService, RailsApiService } from '../../../services';
import { formControl, formGroup, Validators as FormValidators } from '@al00x/forms';
import { BannerEnableValues, BannerTargetValues, BannerTypeValues } from '../../../shared';
import { ReactiveFormsModule } from '@angular/forms';
import { LazyDropdownComponent, LazyDropdownItem } from '../../../components';

export interface BannerDialogData {
    banner?: BannerDetail;
    bannerId?: number;
}

export type BannerDialogResult = boolean;

@Component({
    selector: 'lib-banner-dialog',
    standalone: true,
    imports: [CacDialogLayoutComponent, CacFieldComponent, CacImageUploaderComponent, CacSwitchComponent, ReactiveFormsModule, LazyDropdownComponent],
    templateUrl: './banner-dialog.component.html',
    styleUrl: './banner-dialog.component.scss',
})
export class BannerDialogComponent extends CacBaseDialogComponent<BannerDialogData, BannerDialogResult> {
    private readonly bannerApi = inject(BannerApiService);
    private readonly categoryApi = inject(CategoryApiService);
    private readonly productApi = inject(AdminProductsApiService);
    private readonly railsApi = inject(RailsApiService);

    image = formControl<string | UploadResult>(null, FormValidators.required);

    form = formGroup({
        titleAr: formControl<string>('', FormValidators.required),
        titleEn: formControl<string>('', FormValidators.required),
        descriptionAr: formControl<string>('', FormValidators.required),
        descriptionEn: formControl<string>('', FormValidators.required),
        target: formControl<BannerTarget>(0, FormValidators.required),
        type: formControl<BannerType>(0, FormValidators.required),
        categoryId: formControl<number | undefined>(undefined),
        productId: formControl<number | undefined>(undefined),
        brandId: formControl<number | undefined>(undefined),
        railId: formControl<number | undefined>(undefined),
        status: formControl<boolean>(true, FormValidators.required),
        order: formControl<number>(0, FormValidators.required),
    });

    // Track selected product for dropdown initialization
    selectedProductIds: number[] = [];

    // Observable items for dropdowns
    targetItems = BannerTargetValues;
    typeItems = BannerTypeValues;
    statusItems = BannerEnableValues;
    categoryItems$ = this.categoryApi.getFlatCategories();
    brandItems$ = this.bannerApi.getBrandItems();
    railItems$ = this.railsApi.getRailItems();

    // Product dropdown configuration
    productApiAdapter = this.productApi.getProductNamesAdapter();
    productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
        id: product.id,
        label: product.name.english || product.name.arabic || 'Unnamed Product',
        value: product.id,
        subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
        image: product.productFirstImage
    });

    // Helper methods for field visibility
    shouldShowCategoryField(): boolean {
        return this.form.controls.type.value === 1; // Category
    }

    shouldShowProductField(): boolean {
        return this.form.controls.type.value === 0; // Product
    }

    shouldShowBrandField(): boolean {
        return this.form.controls.type.value === 2; // Brand
    }

    shouldShowRailField(): boolean {
        return this.form.controls.type.value === 3; // Rail
    }

    // Get current selected product IDs for the dropdown
    getCurrentSelectedProductIds(): number[] {
        const productId = this.form.controls.productId.value;
        return productId ? [productId] : this.selectedProductIds;
    }

    // Handle product selection from dropdown
    onProductSelectionChange(selectedProduct: LazyDropdownItem | LazyDropdownItem[] | null) {
        if (selectedProduct) {
            // Since we're using single selection, it should be a single item
            const product = Array.isArray(selectedProduct) ? selectedProduct[0] : selectedProduct;
            this.form.controls.productId.setValue(product?.id);
            this.selectedProductIds = product?.id ? [product.id] : [];
        } else {
            this.form.controls.productId.setValue(undefined);
            this.selectedProductIds = [];
        }
    }
    onSubmit(e: ButtonClickEvent) {
        // Mark all fields as touched to show validation errors
        this.form.markAllAsTouched();
        this.image.markAsTouched();

        // Check if form and image are valid
        if (!this.form.valid || !this.image.valid) {
            return;
        }

        const values = this.form.value;
        if (!values) return;

        const params = this.data.banner ? this.getBannerUpdateParams() : this.getBannerCreateParams();
        if (!params) return;

        const request = this.data.banner
            ? this.bannerApi.updateBanner(params as BannerUpdateParams)
            : this.bannerApi.create(params as BannerCreateParams);

        return request.pipe(e.pipe()).subscribe(() => this.submit(true));
    }


    get options() {
        return {
            fileType: 'Banner' as FileType,
        };
    }



    constructor() {
        super();

        // Set up form subscriptions for validation
        this.form.controls.type.valueChanges.pipe(takeUntilDestroyed()).subscribe((type) => {
            this.updateFieldValidation(type);
        });

        // Initialize form with existing data if editing
        const banner = this.data.banner;
        if (banner) {
            this.image.patchValue(banner.image?.url);
            this.form.patchValue({
                titleAr: banner.title.arabic ?? '',
                descriptionAr: banner.description.arabic ?? '',
                titleEn: banner.title.english ?? '',
                descriptionEn: banner.description.english ?? '',
                status: banner.status == 1 ? true : false,
                target: banner.target,
                categoryId: banner.categoryId,
                productId: banner.productId,
                brandId: banner.brandId,
                railId: banner.railId,
                type: banner.bannerType,
                order: banner.order ?? 0,
            });

            // Set selected product IDs for dropdown initialization
            if (banner.productId) {
                this.selectedProductIds = [banner.productId];
            }
        }

        // Set initial validation
        this.updateFieldValidation(this.form.controls.type.value);
    }

    private updateFieldValidation(type: BannerType) {
        const categoryControl = this.form.controls.categoryId;
        const productControl = this.form.controls.productId;
        const brandControl = this.form.controls.brandId;
        const railControl = this.form.controls.railId;

        // Clear existing validators
        categoryControl.clearValidators();
        productControl.clearValidators();
        brandControl.clearValidators();
        railControl.clearValidators();

        if (type === 1) { // Category
            categoryControl.addValidators(FormValidators.required);
            productControl.setValue(undefined);
            brandControl.setValue(undefined);
            railControl.setValue(undefined);
            this.selectedProductIds = []; // Clear selected products
        } else if (type === 0) { // Product
            productControl.addValidators(FormValidators.required);
            categoryControl.setValue(undefined);
            brandControl.setValue(undefined);
            railControl.setValue(undefined);
        } else if (type === 2) { // Brand
            brandControl.addValidators(FormValidators.required);
            categoryControl.setValue(undefined);
            productControl.setValue(undefined);
            railControl.setValue(undefined);
            this.selectedProductIds = []; // Clear selected products
        } else if (type === 3) { // Rail
            railControl.addValidators(FormValidators.required);
            categoryControl.setValue(undefined);
            productControl.setValue(undefined);
            brandControl.setValue(undefined);
            this.selectedProductIds = []; // Clear selected products
        }

        categoryControl.updateValueAndValidity();
        productControl.updateValueAndValidity();
        brandControl.updateValueAndValidity();
        railControl.updateValueAndValidity();
    }
    getBannerCreateParams(): BannerCreateParams | undefined {
        const values = this.form.value;
        if (!values) return;
        return {
            fields: {
                title: {
                    arabic: values.titleAr!,
                    english: values.titleEn!,
                },
                description: {
                    arabic: values.descriptionAr!,
                    english: values.descriptionEn!,
                },
                status: values.status ? 1 : 0,
                target: values.target!,
                type: values.type!,
                thumbnailPath: this.getImagePath(),
                categoryId: values.categoryId ?? null,
                productId: values.productId ?? null,
                brandId: values.brandId ?? null,
                railId: values.railId ?? null,
                order: values.order ?? 0
            }
        };
    }
    getBannerUpdateParams(): BannerUpdateParams | undefined {
        const values = this.form.value;
        if (!values) return;

        return {
            bannerId: this.data.bannerId ?? this.data.banner!.id,
            fields: {
                status: {
                    value: values.status ? 1 : 0,
                },
                categoryId: values.categoryId ? {
                    value: values.categoryId,
                } : null,
                productId: values.productId ? {
                    value: values.productId,
                } : null,
                brandId: values.brandId ? {
                    value: values.brandId,
                } : null,
                railId: values.railId ? {
                    value: values.railId,
                } : null,
                target: {
                    value: values.target!,
                },
                type: {
                    value: values.type!,
                },
                thumbnailPath: {
                    value: this.getImagePath()
                },
                description: {
                    value: {
                        arabic: values.descriptionAr!,
                        english: values.descriptionEn!,
                    },
                },
                title: {
                    value: {
                        arabic: values.titleAr!,
                        english: values.titleEn!,
                    },
                },
                order: {
                    value: values.order ?? 0
                }
            }
        };
    }
    getImagePath(): string {
        const imageValue = this.image.value
        const imagePath = (typeof imageValue === 'string' ? this.data.banner?.image?.path : imageValue?.imagePath) ?? null;
        return imagePath!;
    }

}
